import type { QueryClient } from '@tanstack/react-query'

export const CacheScope = {
  /** Cleared on logout - auth and admin session data */
  LOGIN: 'login',
  /** Cleared when admin switches/exits business */
  BUSINESS: 'business',
  /** Never cleared (shared data like pricing) */
  PERMANENT: 'permanent',
} as const

export type CacheScope = (typeof CacheScope)[keyof typeof CacheScope]

/**
 * Every query key used in the app MUST be registered here - single source
 * of truth for type checking and cache clearing.
 */
export const QueryKey = {
  AUTH: ['auth'],
  AUTH_CURRENT_USER: ['auth', 'current-user'],
  AUTH_EVENT_EMAILS: ['auth', 'event-emails'],
  ADMIN: ['admin'],
  ADMIN_CURRENT_USER: ['admin', 'current-user'],
  ADMIN_BUSINESSES: ['admin', 'businesses'],
  ADMIN_CONTENT_REPORTS: ['admin', 'content-reports'],
  ADMIN_MODERATION_REPORTS: ['admin', 'moderation-reports'],
  ADMIN_CHANNEL: ['admin', 'channel'],
  ADMIN_DROPS: ['admin', 'drops'],
  ADMIN_STATS: ['admin', 'stats'],
  ADMIN_SIGNUPS: ['admin', 'signups'],
  ADMIN_GEO: ['admin', 'geo'],
  ADMIN_GROWTH: ['admin', 'growth'],
  ADMIN_DOWNLOAD_LINKS: ['admin', 'download-links'],
  ADMIN_PARTNERS: ['admin', 'partners'],
  ADMIN_SHARE_CASHBACK: ['admin', 'share-cashback'],
  ANALYTICS: ['analytics'],
  BUSINESS: ['business'],
  BUSINESS_CURRENT: ['business', 'current'],
  BUSINESS_DETAIL: ['business'],
  AI_BRAND: ['ai-studio', 'brand'],
  BUSINESS_OVERVIEW: ['stats', 'business-overview'],
  BUSINESS_PROFILE_LINKS: ['business', 'profile-links'],
  TICKET_SETTINGS: ['business', 'ticket-settings'],
  TICKET_EARNINGS: ['business', 'ticket-earnings'],
  CREDITS: ['credits'],
  CREDITS_BALANCE: ['credits', 'balance'],
  CREDITS_CASHBACK: ['credits', 'cashback'],
  CROSSPOST_CONNECTIONS: ['crosspost', 'connections'],
  CROSSPOST_POSTS: ['crosspost', 'posts'],
  CROSSPOST_STATUS: ['crosspost', 'status'],
  EVENTS: ['events'],
  ORGANIZER_PROFILE: ['organizer', 'profile'],
  ORGANIZER_MEMBERS: ['organizer', 'members'],
  ORGANIZATION_PROFILES: ['organization', 'profiles'],
  BILLING_ENTITIES: ['organization', 'billing-entities'],
  EVENT: ['event'],
  EVENT_CHECKIN_CODE: ['event', 'checkin-code'],
  EVENT_LIVE_ROOM: ['event', 'live-room'],
  EVENT_REGISTRATION: ['event', 'registration'],
  EVENT_REGISTRATIONS: ['event', 'registrations'],
  EVENT_ROOM_REQUESTS: ['event', 'room-requests'],
  EVENT_SUMMARY: ['event', 'summary'],
  EVENT_TICKET_ORDERS: ['event', 'ticket-orders'],
  EVENT_TICKET_SUMMARY: ['event', 'ticket-summary'],
  EVENT_TICKET_TYPES: ['event', 'ticket-types'],
  GENERATED_ASSETS: ['generated-assets'],
  LISTINGS: ['listings'],
  LISTING: ['listing'],
  LISTING_ACTIONS: ['listing-actions'],
  LISTING_BUTTONS: ['listing-buttons'],
  LISTING_GOOD_FOR: ['listing-good-for'],
  LISTING_MENUS: ['listing-menus'],
  LISTING_VENUE_ROOM: ['listing', 'venue-room'],
  PUBLIC_STATS: ['stats', 'public'],
  SHARE_LINKS: ['share-links'],
  SUBSCRIPTION: ['subscription'],
  SUBSCRIPTION_STATUS: ['subscription', 'status'],
  SUBSCRIPTION_PRICING: ['subscription', 'pricing'],
  TAGS: ['tags'],
} as const

type QueryKeyPrefix = (typeof QueryKey)[keyof typeof QueryKey]

export function queryKey<T extends QueryKeyPrefix>(
  prefix: T,
  ...rest: unknown[]
): readonly unknown[] {
  return rest.length ? [...prefix, ...rest] : prefix
}

const SCOPE_PREFIXES: Record<CacheScope, readonly QueryKeyPrefix[]> = {
  [CacheScope.LOGIN]: [QueryKey.AUTH, QueryKey.ADMIN],
  [CacheScope.BUSINESS]: [
    QueryKey.AUTH_CURRENT_USER,
    QueryKey.ANALYTICS,
    QueryKey.BUSINESS,
    QueryKey.BUSINESS_OVERVIEW,
    QueryKey.CREDITS,
    QueryKey.CROSSPOST_CONNECTIONS,
    QueryKey.CROSSPOST_POSTS,
    QueryKey.CROSSPOST_STATUS,
    QueryKey.EVENTS,
    QueryKey.EVENT,
    QueryKey.GENERATED_ASSETS,
    QueryKey.LISTINGS,
    QueryKey.LISTING,
    QueryKey.LISTING_ACTIONS,
    QueryKey.LISTING_BUTTONS,
    QueryKey.LISTING_GOOD_FOR,
    QueryKey.LISTING_MENUS,
    QueryKey.ORGANIZATION_PROFILES,
    QueryKey.ORGANIZER_MEMBERS,
    QueryKey.SHARE_LINKS,
    QueryKey.SUBSCRIPTION_STATUS,
    QueryKey.TAGS,
  ],
  [CacheScope.PERMANENT]: [QueryKey.SUBSCRIPTION_PRICING],
}

export function clearScope(queryClient: QueryClient, scope: CacheScope): void {
  const prefixes = SCOPE_PREFIXES[scope]
  for (const prefix of prefixes) {
    queryClient.removeQueries({ queryKey: prefix })
  }
}
